import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { createContext, forwardRef, isValidElement, cloneElement, useMemo, useState, useEffect, useRef, useCallback, useContext, Children, useId } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { EmptyState } from '@norkron/web-states';
import { useRovingTabIndex, useTypeahead, useDisclosure } from '@norkron/web-a11y';
import { useFloating } from '@norkron/web-floating';
import { useDismiss } from '@norkron/web-dismiss';
import { useScrollLock, Portal } from '@norkron/web-portal';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import '@norkron/web-tooltip';
import { useControlledState } from '@norkron/web-controlled-state';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/error-codes.ts
var WEB_MENU_LIB = "@norkron/web-menu";
var WEB_MENU_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-menu");
var { warn, panic, reportInvalidInput } = createScopedErrors(WEB_MENU_LIB, WEB_MENU_TOKEN);
try {
  registerCodes(WEB_MENU_LIB, {
    WEB_MENU_INVALID_INPUT: {
      description: "Runtime input validation rejected the value passed to a public web-menu export (Wave 10 zod mandate). Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-menu/invalid-input",
      exampleFix: "Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib."
    },
    WEB_MENU_TRIGGER_DETACHED: {
      description: "A Menu subcomponent (MenuTrigger, MenuContent, MenuItem, MenuSeparator, MenuSubMenu) was rendered outside the <Menu> provider tree. The component still renders, but keyboard navigation + ARIA wiring will be inert.",
      severity: "warning",
      docsAnchor: "web-menu/trigger-detached",
      exampleFix: "Wrap the subcomponent in <Menu>...</Menu> so the disclosure + roving-tabindex context is available."
    },
    WEB_MENU_EMPTY: {
      description: "MenuContent was rendered with zero MenuItem children. The library substitutes an EmptyState so the menu surface is not invisible, but the warning surfaces the gap so a developer can add items or hide the menu trigger entirely.",
      severity: "warning",
      docsAnchor: "web-menu/empty",
      exampleFix: "Render <MenuItem> children, or guard the <Menu> with a condition that hides it when the action list is empty."
    },
    WEB_MENU_ASCHILD_INVALID_CHILD: {
      description: "MenuItem (or MenuTrigger) was given `asChild` but `children` is not a single React element \u2014 a string, number, fragment array, or nullish node cannot receive the cloned role/ref/handler props. The component falls back to its default wrapper render so the row still appears, but the consumer did not get the polymorphic element they asked for.",
      severity: "warning",
      docsAnchor: "web-menu/aschild-invalid-child",
      exampleFix: 'Pass exactly one element child: <MenuItem label="Download" asChild><a href="/x" download>Download</a></MenuItem>. Drop `asChild` when the row content is plain text.'
    },
    // Self-check audit-channel codes (emitted by selfCheck() in src/self-check.ts).
    // Registered here so the substrate-gate's anti-fabrication check
    // (loadFlowManifest -> isCodeRegistered) can resolve scenario manifests
    // declaring expectedFindings[].code against this lib.
    MN_KEYBOARD_NAV_BROKEN: {
      description: 'selfCheck observed a role="menu" element whose direct children include one or more elements without an APG-valid role (menuitem / menuitemradio / menuitemcheckbox / separator / group / none / presentation). Arrow-key roving traversal will skip those children and the APG menu pattern is broken. Audit-channel finding emitted at major severity per src/self-check.ts.',
      severity: "warning",
      docsAnchor: "web-menu/keyboard-nav-broken",
      exampleFix: 'Stamp every clickable child of role="menu" with the appropriate role="menuitem" (or radio/checkbox variant); move non-menuitem affordances outside the menu surface.'
    },
    MN_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking menu instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-menu/self-check#budget-exceeded"
    },
    MN_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a menu instance; reported as a finding with severity info.",
      severity: "info",
      docsAnchor: "web-menu/self-check#internal-error"
    }
  });
} catch {
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-menu";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-menu"]')
    );
    if (instances.length === 0) return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "MN_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (instance.getAttribute("role") === "menu") {
        const children = Array.from(instance.children);
        const bad = children.filter((c) => {
          const r = c.getAttribute("role");
          if (!r) return true;
          return !["menuitem", "menuitemradio", "menuitemcheckbox", "separator", "group", "none", "presentation"].includes(r);
        });
        if (bad.length > 0) {
          findings.push({
            libId: LIB_ID,
            code: "MN_KEYBOARD_NAV_BROKEN",
            category: "a11y",
            severity: "major",
            message: `Menu has ${bad.length} child element(s) without role="menuitem"/"menuitemradio"/"menuitemcheckbox". Arrow-key navigation will skip them and the APG menu pattern is broken.`,
            selector: selectorFor(instance),
            suggestedFix: 'Stamp every clickable child with the appropriate role="menuitem" variant; non-menuitem affordances belong outside role="menu".',
            extras: { offendingCount: bad.length }
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "MN_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.4.0";

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var MenuPlacementSchema = z.enum([
  "top",
  "top-start",
  "top-end",
  "bottom",
  "bottom-start",
  "bottom-end",
  "left",
  "right"
]);
var MenuPropsSchema = z.object({
  defaultOpen: z.boolean().optional(),
  open: z.boolean().optional(),
  onOpenChange: z.function().optional(),
  placement: MenuPlacementSchema.optional(),
  id: z.string().optional(),
  loop: z.boolean().optional(),
  typeahead: z.boolean().optional(),
  modal: z.boolean().optional(),
  children: z.unknown().optional()
});
var MenuTriggerPropsSchema = z.object({
  children: z.unknown().optional(),
  asChild: z.boolean().optional(),
  /**
   * Accessible name for the trigger. Forwarded as `aria-label` on the
   * underlying button. When omitted AND the visible text is empty /
   * single-character / a known icon-glyph, MenuTrigger auto-derives a
   * safe default ("Open menu") so the trigger never violates
   * `STR_TOOLTIP_TRIGGER_WITHOUT_ACCESSIBLE_LABEL` for minimal-API /
   * matrix examples. Pass an explicit string to override.
   */
  ariaLabel: z.string().optional()
});
var MenuContentPropsSchema = z.object({
  children: z.unknown().optional(),
  offset: z.number().nonnegative().optional(),
  className: z.string().optional(),
  ariaLabel: z.string().optional(),
  loop: z.boolean().optional(),
  typeahead: z.boolean().optional()
});
var MenuItemPropsSchema = z.object({
  label: z.string().min(1),
  onSelect: z.function().optional(),
  disabled: z.boolean().optional(),
  variant: z.enum(["menuitem", "menuitemcheckbox", "menuitemradio"]).optional(),
  checked: z.boolean().optional(),
  className: z.string().optional(),
  id: z.string().optional(),
  closeOnSelect: z.boolean().optional(),
  shortcut: z.string().optional(),
  description: z.string().optional(),
  icon: z.unknown().optional(),
  textValue: z.string().optional(),
  /**
   * Render the single `children` element AS the menu item instead of
   * wrapping it in the library's `<div role="menuitem">`. Mirrors
   * `MenuTriggerProps.asChild`. MUST be declared here — zod runs in
   * `strip` mode, so an undeclared prop is silently dropped before
   * MenuItem ever sees it.
   */
  asChild: z.boolean().optional(),
  children: z.unknown().optional()
});
var MenuSubMenuPropsSchema = z.object({
  label: z.string().min(1),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
  openOnHover: z.boolean().optional(),
  hoverOpenDelay: z.number().nonnegative().optional(),
  hoverCloseDelay: z.number().nonnegative().optional(),
  children: z.unknown().optional()
});
var MenuSeparatorPropsSchema = z.object({
  className: z.string().optional()
});
var UseMenuOptionsSchema = z.object({
  defaultOpen: z.boolean().optional(),
  onOpenChange: z.function().optional(),
  id: z.string().optional()
});
var MenuBarPropsSchema = z.object({
  children: z.unknown().optional(),
  ariaLabel: z.string().optional(),
  id: z.string().optional(),
  className: z.string().optional(),
  loop: z.boolean().optional(),
  placement: MenuPlacementSchema.optional(),
  testId: z.string().optional()
});
var MenuBarMenuPropsSchema = z.object({
  label: z.string().min(1),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
  id: z.string().optional(),
  testId: z.string().optional(),
  children: z.unknown().optional()
});

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_MENU_LIB,
      libToken: WEB_MENU_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
    return input;
  }
  return parsed.data;
}
function useMenu(options = {}) {
  const validated = validateInput(
    UseMenuOptionsSchema,
    "useMenu",
    options
  );
  const { defaultOpen = false, onOpenChange, id } = validated;
  const autoId = useId();
  const baseId = id ?? autoId;
  const { open, show, hide, toggle } = useDisclosure({
    defaultOpen,
    onChange: onOpenChange
  });
  return useMemo(
    () => ({
      open,
      show,
      hide,
      toggle,
      triggerId: `${baseId}-trigger`,
      contentId: `${baseId}-content`
    }),
    [open, show, hide, toggle, baseId]
  );
}
function useControlledMenu(controlled, defaultOpen, onChange) {
  const { value: open, setValue: setOpen } = useControlledState(controlled, defaultOpen, onChange);
  return { open, setOpen };
}
registerLazy(
  WEB_MENU_LIB,
  () => selfCheck
);
var MenuItemContext = createContext(null);
var MenuContext = createContext(null);
function useMenuContext(fn) {
  const ctx = useContext(MenuContext);
  if (!ctx) {
    warn({
      fn,
      code: "WEB_MENU_TRIGGER_DETACHED",
      message: `${fn} rendered outside <Menu>`
    });
  }
  return ctx;
}
function Menu(props) {
  const validated = validateInput(
    MenuPropsSchema,
    "Menu",
    props
  );
  const {
    defaultOpen = false,
    open: controlled,
    onOpenChange,
    placement = "bottom-start",
    id,
    loop = false,
    typeahead = true,
    modal = false,
    children
  } = validated;
  const state = useMenu({ defaultOpen, id, onOpenChange });
  const { open, setOpen } = useControlledMenu(
    controlled,
    defaultOpen,
    onOpenChange
  );
  const triggerRef = useRef(null);
  const radioGroupValues = useRef(/* @__PURE__ */ new Map());
  const [, forceRadioRender] = useState(0);
  const effective = {
    ...state,
    open: controlled !== void 0 ? open : state.open,
    show: () => {
      if (controlled === void 0) state.show();
      setOpen(true);
    },
    hide: () => {
      if (controlled === void 0) state.hide();
      setOpen(false);
    },
    toggle: () => {
      if (controlled === void 0) state.toggle();
      setOpen(!effective.open);
    }
  };
  const closeOnSelect = useCallback(() => {
    effective.hide();
    queueMicrotask(() => triggerRef.current?.focus?.());
  }, [effective.hide]);
  const registerRadioGroup = useCallback(
    (groupId, value) => {
      if (!radioGroupValues.current.has(groupId)) {
        radioGroupValues.current.set(groupId, value);
      }
    },
    []
  );
  const getRadioGroupValue = useCallback(
    (groupId) => radioGroupValues.current.get(groupId),
    []
  );
  const setRadioGroupValue = useCallback((groupId, value) => {
    radioGroupValues.current.set(groupId, value);
    forceRadioRender((n) => n + 1);
  }, []);
  return /* @__PURE__ */ jsx(
    MenuContext.Provider,
    {
      value: {
        ...effective,
        placement,
        triggerRef,
        closeOnSelect,
        registerRadioGroup,
        getRadioGroupValue,
        setRadioGroupValue,
        loop,
        typeahead,
        modal
      },
      children: /* @__PURE__ */ jsx(
        ErrorBoundary,
        {
          fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }),
          children
        }
      )
    }
  );
}
var MENU_TRIGGER_ICON_GLYPHS = /* @__PURE__ */ new Set([
  "?",
  "!",
  "i",
  "\u2139",
  "\xD7",
  "x",
  "\u2715",
  "\u2716",
  "\u22EF",
  "\u2026",
  "\u22EE",
  "\u2630",
  "\u25BE",
  "\u25BC",
  "\u25BD"
]);
function isSingleCharOrIconLabel(node) {
  if (node == null || node === false) return true;
  if (typeof node === "string") {
    const t = node.trim();
    if (t.length === 0) return true;
    if (t.length === 1) return true;
    if (MENU_TRIGGER_ICON_GLYPHS.has(t)) return true;
    return false;
  }
  if (typeof node === "number") return String(node).length <= 1;
  return true;
}
var MenuTrigger = forwardRef(
  function MenuTrigger2(props, ref) {
    const validated = validateInput(
      MenuTriggerPropsSchema,
      "MenuTrigger",
      props
    );
    const ctx = useMenuContext("MenuTrigger");
    const { children, asChild, ariaLabel } = validated;
    const derivedAriaLabel = ariaLabel ?? (isSingleCharOrIconLabel(children) ? "Open menu" : void 0);
    const onClick = () => ctx?.toggle();
    const onKeyDown = (e) => {
      if (!ctx) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        ctx.show();
      }
    };
    const ariaProps = {
      "aria-haspopup": "menu",
      "aria-expanded": ctx?.open ?? false,
      id: ctx?.triggerId
    };
    if (ctx?.open && ctx.contentId) {
      ariaProps["aria-controls"] = ctx.contentId;
    }
    if (derivedAriaLabel) {
      ariaProps["aria-label"] = derivedAriaLabel;
    }
    if (asChild && isValidElement(children)) {
      const setRef = (node) => {
        if (ctx) ctx.triggerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref)
          ref.current = node;
      };
      const childProps = children.props;
      const existingOnClick = childProps.onClick;
      const existingOnKeyDown = childProps.onKeyDown;
      return cloneElement(children, {
        ...ariaProps,
        ref: setRef,
        onClick: (e) => {
          existingOnClick?.(e);
          onClick();
        },
        onKeyDown: (e) => {
          existingOnKeyDown?.(e);
          onKeyDown(e);
        }
      });
    }
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ref: (node) => {
          if (ctx) ctx.triggerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref)
            ref.current = node;
        },
        className: "menu__trigger",
        "data-allow-unwired": "true",
        ...withConformance(WEB_MENU_LIB, LIB_VERSION),
        onClick,
        onKeyDown,
        ...ariaProps,
        children: /* @__PURE__ */ jsx("span", { className: "menu__trigger-label", children })
      }
    );
  }
);
var MenuContent = forwardRef(
  function MenuContent2(props, ref) {
    const validated = validateInput(
      MenuContentPropsSchema,
      "MenuContent",
      props
    );
    const ctx = useMenuContext("MenuContent");
    const {
      children,
      offset = 6,
      className,
      ariaLabel,
      loop: loopProp,
      typeahead: typeaheadProp
    } = validated;
    const effectiveLoop = loopProp ?? ctx?.loop ?? false;
    const effectiveTypeahead = typeaheadProp ?? ctx?.typeahead ?? true;
    const isModal = ctx?.modal ?? false;
    useScrollLock(Boolean(ctx?.open) && isModal);
    const focusable = useMemo(() => collectFocusable(children), [children]);
    const itemRefs = useMemo(
      () => focusable.map(() => ({ current: null })),
      [focusable]
    );
    const labels = useMemo(
      () => focusable.map((f) => f.textValue),
      [focusable]
    );
    const [activeIndex, setActiveIndex] = useState(0);
    const { tabIndex, onKeyDown: rovingKeyDown } = useRovingTabIndex(
      itemRefs,
      {
        orientation: "vertical",
        loop: effectiveLoop,
        activeIndex,
        onActivate: (i) => {
          setActiveIndex(i);
          focusable[i]?.onActivate?.();
        },
        onMove: setActiveIndex
      }
    );
    const { onKeyDown: typeaheadKeyDown } = useTypeahead(
      labels,
      (label) => label,
      (i) => {
        setActiveIndex(i);
        itemRefs[i]?.current?.focus();
      }
    );
    const floatingPlacement = ctx?.placement ?? "bottom-start";
    const { refs: floatRefs, x, y, update } = useFloating({
      placement: floatingPlacement,
      strategy: "fixed",
      offset
    });
    useEffect(() => {
      if (ctx?.triggerRef.current) floatRefs.setReference(ctx.triggerRef.current);
    }, [ctx?.triggerRef, floatRefs, ctx?.open]);
    const menuDivRef = useRef(null);
    const setMenuRef = useCallback(
      (node2) => {
        menuDivRef.current = node2;
        floatRefs.setFloating(node2);
        if (typeof ref === "function") ref(node2);
        else if (ref)
          ref.current = node2;
      },
      [floatRefs, ref]
    );
    useDismiss({
      enabled: Boolean(ctx?.open),
      refs: [menuDivRef, ctx?.triggerRef ?? { current: null }],
      onDismiss: () => {
        ctx?.hide();
        ctx?.triggerRef.current?.focus?.();
      }
    });
    useEffect(() => {
      if (!ctx?.open) return;
      update();
      const first = itemRefs.find((r) => r.current);
      if (first?.current) first.current.focus();
    }, [ctx?.open, itemRefs, update]);
    if (!ctx?.open) return null;
    const body = focusable.length === 0 ? (() => {
      warn({
        fn: "MenuContent",
        code: "WEB_MENU_EMPTY",
        message: "MenuContent rendered without any MenuItem children"
      });
      return /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: "No actions",
          description: "This menu has no items."
        }
      );
    })() : renderItemsWithRefs(children, itemRefs, focusable, tabIndex);
    const style = {
      position: "fixed",
      top: y ?? 0,
      left: x ?? 0,
      visibility: x === null || y === null ? "hidden" : void 0
    };
    const cls = ["menu__content", `menu__content--${ctx.placement}`, className].filter(Boolean).join(" ");
    const onMenuKeyDown = (e) => {
      rovingKeyDown(e);
      if (effectiveTypeahead) typeaheadKeyDown(e);
    };
    const overlay = isModal ? /* @__PURE__ */ jsx(
      "div",
      {
        className: "menu__overlay",
        "data-menu-overlay": "true",
        "aria-hidden": "true",
        onClick: () => {
          ctx?.hide();
          ctx?.triggerRef.current?.focus?.();
        },
        style: {
          position: "fixed",
          inset: 0,
          background: "transparent",
          // Sit just below the menu surface (which uses default
          // stacking inside the portal). Pointer-events: auto so it
          // catches clicks before they reach background elements.
          pointerEvents: "auto",
          zIndex: 0
        }
      }
    ) : null;
    const node = /* @__PURE__ */ jsx(
      "div",
      {
        ref: setMenuRef,
        role: "menu",
        id: ctx.contentId,
        "aria-labelledby": ctx.triggerId,
        "aria-label": ariaLabel,
        className: cls,
        style,
        "data-state": ctx.open ? "open" : "closed",
        "data-placement": ctx.placement,
        "data-keep-clear": "true",
        onKeyDown: onMenuKeyDown,
        ...withConformance(WEB_MENU_LIB, LIB_VERSION),
        children: body
      }
    );
    return /* @__PURE__ */ jsxs(Portal, { children: [
      overlay,
      node
    ] });
  }
);
var MenuItem = forwardRef(
  function MenuItem2(props, ref) {
    const validated = validateInput(
      MenuItemPropsSchema,
      "MenuItem",
      props
    );
    const ctx = useContext(MenuContext);
    const {
      label,
      onSelect,
      disabled = false,
      variant = "menuitem",
      checked,
      className,
      id,
      closeOnSelect,
      shortcut,
      description,
      icon,
      asChild,
      children
    } = validated;
    const onClick = () => {
      if (disabled) return;
      onSelect?.();
      const shouldClose = closeOnSelect ?? variant === "menuitem";
      if (shouldClose) ctx?.closeOnSelect();
    };
    const cls = [
      "menu__item",
      `menu__item--${variant}`,
      disabled && "menu__item--disabled",
      variant !== "menuitem" && checked && "menu__item--checked",
      (icon || description) && "menu__item--rich",
      className
    ].filter(Boolean).join(" ");
    const ariaProps = {
      role: variant,
      tabIndex: -1,
      id
    };
    if (variant === "menuitemcheckbox" || variant === "menuitemradio") {
      ariaProps["aria-checked"] = Boolean(checked);
    }
    if (disabled) ariaProps["aria-disabled"] = true;
    if (shortcut) ariaProps["aria-keyshortcuts"] = shortcut;
    const hasRich = Boolean(icon || description || shortcut);
    const body = children ?? (hasRich ? /* @__PURE__ */ jsxs(Fragment, { children: [
      icon ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "menu__item-icon", children: icon }) : null,
      /* @__PURE__ */ jsxs("span", { className: "menu__item-body", children: [
        /* @__PURE__ */ jsx("span", { className: "menu__item-label", children: label }),
        description ? /* @__PURE__ */ jsx("span", { className: "menu__item-description", children: description }) : null
      ] }),
      shortcut ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "menu__item-shortcut", children: shortcut }) : null
    ] }) : label);
    if (asChild) {
      if (isValidElement(children)) {
        const childProps = children.props;
        const existingOnClick = childProps.onClick;
        const existingClassName = childProps.className;
        const existingRef = (children.ref ?? childProps.ref) || void 0;
        const setRef = (node) => {
          if (typeof ref === "function") ref(node);
          else if (ref)
            ref.current = node;
          if (typeof existingRef === "function") existingRef(node);
          else if (existingRef && "current" in existingRef) {
            existingRef.current = node;
          }
        };
        return cloneElement(children, {
          ...ariaProps,
          className: [cls, existingClassName].filter(Boolean).join(" "),
          "data-menu-item-label": label,
          ref: setRef,
          onClick: (e) => {
            existingOnClick?.(e);
            onClick();
          }
        });
      }
      warn({
        fn: "MenuItem",
        code: "WEB_MENU_ASCHILD_INVALID_CHILD",
        message: 'MenuItem asChild requires a single React element child; falling back to the default <div role="menuitem"> render'
      });
    }
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cls,
        onClick,
        "data-menu-item-label": label,
        ...ariaProps,
        children: body
      }
    );
  }
);
function MenuSeparator(props) {
  const validated = validateInput(
    MenuSeparatorPropsSchema,
    "MenuSeparator",
    props
  );
  const { className } = validated;
  const cls = ["menu__separator", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { role: "separator", className: cls });
}
function MenuGroup(props) {
  const { label, sticky = true, className, children } = props;
  const cls = ["menu__group", className].filter(Boolean).join(" ");
  const labelId = useMemo(
    () => label ? `menu-group-label-${Math.random().toString(36).slice(2, 9)}` : void 0,
    [label]
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "group",
      "aria-label": label,
      "aria-labelledby": labelId,
      className: cls,
      children: [
        label ? /* @__PURE__ */ jsx(
          "div",
          {
            id: labelId,
            role: "presentation",
            className: [
              "menu__group-label",
              sticky && "menu__group-label--sticky"
            ].filter(Boolean).join(" "),
            children: label
          }
        ) : null,
        children
      ]
    }
  );
}
function MenuLabel(props) {
  const { className, children } = props;
  const cls = ["menu__label", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { className: cls, role: "presentation", children });
}
var RadioGroupContext = createContext(null);
function MenuRadioGroup(props) {
  const { value, onValueChange, id, className, children } = props;
  const ctx = useContext(MenuContext);
  const fallbackId = useMemo(
    () => id ?? `menu-radio-group-${Math.random().toString(36).slice(2, 9)}`,
    [id]
  );
  const isControlled = value !== void 0;
  const liveValue = isControlled ? value : ctx?.getRadioGroupValue(fallbackId);
  useEffect(() => {
    if (!isControlled) ctx?.registerRadioGroup(fallbackId, void 0);
  }, [ctx, fallbackId, isControlled]);
  const onChange = useCallback(
    (next) => {
      if (!isControlled) ctx?.setRadioGroupValue(fallbackId, next);
      onValueChange?.(next);
    },
    [ctx, fallbackId, isControlled, onValueChange]
  );
  const cls = ["menu__radio-group", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    RadioGroupContext.Provider,
    {
      value: { value: liveValue, groupId: fallbackId, onValueChange: onChange },
      children: /* @__PURE__ */ jsx("div", { role: "group", className: cls, children })
    }
  );
}
var DEFAULT_INDICATOR_GLYPH = /* @__PURE__ */ jsx(
  "svg",
  {
    "aria-hidden": "true",
    focusable: "false",
    width: "0.875em",
    height: "0.875em",
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    children: /* @__PURE__ */ jsx("polyline", { points: "3 8 7 12 13 4" })
  }
);
function MenuItemIndicator(props) {
  const { forceMount = false, className, children } = props;
  const parent = useContext(MenuItemContext);
  if (!parent) {
    warn({
      fn: "MenuItemIndicator",
      code: "WEB_MENU_TRIGGER_DETACHED",
      message: "MenuItemIndicator rendered outside <MenuItemCheckbox>/<MenuRadioItem>"
    });
    return null;
  }
  if (!parent.checked && !forceMount) return null;
  const cls = ["menu__item-indicator", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "span",
    {
      "aria-hidden": "true",
      className: cls,
      "data-state": parent.checked ? "checked" : "unchecked",
      children: children ?? DEFAULT_INDICATOR_GLYPH
    }
  );
}
function hasIndicatorChild(children) {
  let found = false;
  Children.forEach(children, (child) => {
    if (found) return;
    if (isValidElement(child) && child.type === MenuItemIndicator) {
      found = true;
    }
  });
  return found;
}
var MenuRadioItem = forwardRef(
  function MenuRadioItem2(props, ref) {
    const { label, value, disabled = false, className, id, children } = props;
    const group = useContext(RadioGroupContext);
    const checked = group?.value === value;
    const hasIndicator = hasIndicatorChild(children);
    const suppressCss = hasIndicator;
    const itemCls = [
      suppressCss && "menu__item--indicator-slotted",
      className
    ].filter(Boolean).join(" ") || void 0;
    return /* @__PURE__ */ jsx(
      MenuItemContext.Provider,
      {
        value: { checked: Boolean(checked), variant: "menuitemradio" },
        children: /* @__PURE__ */ jsx(
          MenuItem,
          {
            ref,
            label,
            variant: "menuitemradio",
            checked,
            disabled,
            className: itemCls,
            id,
            closeOnSelect: false,
            onSelect: () => group?.onValueChange(value),
            children
          }
        )
      }
    );
  }
);
var MenuItemCheckbox = forwardRef(
  function MenuItemCheckbox2(props, ref) {
    const {
      label,
      checked,
      onCheckedChange,
      disabled,
      className,
      id,
      shortcut,
      description,
      icon,
      children
    } = props;
    const hasIndicator = hasIndicatorChild(children);
    const itemCls = [
      hasIndicator && "menu__item--indicator-slotted",
      className
    ].filter(Boolean).join(" ") || void 0;
    return /* @__PURE__ */ jsx(
      MenuItemContext.Provider,
      {
        value: { checked: Boolean(checked), variant: "menuitemcheckbox" },
        children: /* @__PURE__ */ jsx(
          MenuItem,
          {
            ref,
            label,
            variant: "menuitemcheckbox",
            checked,
            disabled,
            className: itemCls,
            id,
            shortcut,
            description,
            icon,
            closeOnSelect: false,
            onSelect: () => onCheckedChange?.(!checked),
            children
          }
        )
      }
    );
  }
);
function MenuSubMenu(props) {
  const validated = validateInput(
    MenuSubMenuPropsSchema,
    "MenuSubMenu",
    props
  );
  const {
    label,
    disabled = false,
    className,
    openOnHover = true,
    hoverOpenDelay = 120,
    hoverCloseDelay = 200,
    children
  } = validated;
  const [open, setOpen] = useState(false);
  const hoverTimerRef = useRef(null);
  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);
  const onRowPointerEnter = useCallback(() => {
    if (!openOnHover || disabled) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setOpen(true), hoverOpenDelay);
  }, [openOnHover, disabled, hoverOpenDelay, clearHoverTimer]);
  const onRowPointerLeave = useCallback(() => {
    if (!openOnHover) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setOpen(false), hoverCloseDelay);
  }, [openOnHover, hoverCloseDelay, clearHoverTimer]);
  const onPanelPointerEnter = useCallback(() => {
    if (!openOnHover) return;
    clearHoverTimer();
  }, [openOnHover, clearHoverTimer]);
  const onPanelPointerLeave = useCallback(() => {
    if (!openOnHover) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setOpen(false), hoverCloseDelay);
  }, [openOnHover, hoverCloseDelay, clearHoverTimer]);
  const rowRef = useRef(null);
  const panelRef = useRef(null);
  const subRefs = useMemo(() => collectFocusable(children), [children]);
  const subItemRefs = useMemo(
    () => subRefs.map(() => ({ current: null })),
    [subRefs]
  );
  const subLabels = useMemo(() => subRefs.map((f) => f.textValue), [subRefs]);
  const [subActive, setSubActive] = useState(0);
  const { refs: subFloat, x, y, update: subUpdate } = useFloating({
    placement: "right-start",
    strategy: "fixed",
    offset: 4
  });
  useEffect(() => {
    if (rowRef.current) subFloat.setReference(rowRef.current);
  }, [subFloat, open]);
  const { tabIndex, onKeyDown: rovingSubKeyDown } = useRovingTabIndex(
    subItemRefs,
    {
      orientation: "vertical",
      loop: true,
      activeIndex: subActive,
      onActivate: (i) => {
        setSubActive(i);
        subRefs[i]?.onActivate?.();
        setOpen(false);
        rowRef.current?.focus();
      },
      onMove: setSubActive
    }
  );
  const { onKeyDown: subTypeaheadKeyDown } = useTypeahead(
    subLabels,
    (l) => l,
    (i) => {
      setSubActive(i);
      subItemRefs[i]?.current?.focus();
    }
  );
  useDismiss({
    enabled: open,
    refs: [panelRef, rowRef],
    onDismiss: () => {
      setOpen(false);
      rowRef.current?.focus();
    }
  });
  useEffect(() => {
    if (!open) return;
    subUpdate();
    queueMicrotask(() => subItemRefs[0]?.current?.focus());
  }, [open, subItemRefs, subUpdate]);
  const onRowKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === "ArrowRight" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
    },
    [disabled]
  );
  const onSubKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setOpen(false);
        rowRef.current?.focus();
        return;
      }
      rovingSubKeyDown(e);
      subTypeaheadKeyDown(e);
    },
    [rovingSubKeyDown, subTypeaheadKeyDown]
  );
  const cls = [
    "menu__item",
    "menu__submenu-trigger",
    disabled && "menu__item--disabled",
    open && "menu__submenu-trigger--open",
    className
  ].filter(Boolean).join(" ");
  const setPanelRef = useCallback(
    (node) => {
      panelRef.current = node;
      subFloat.setFloating(node);
    },
    [subFloat]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: rowRef,
        role: "menuitem",
        className: cls,
        tabIndex: -1,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-disabled": disabled || void 0,
        "data-menu-item-label": label,
        onClick: () => !disabled && setOpen((o) => !o),
        onKeyDown: onRowKeyDown,
        onPointerEnter: onRowPointerEnter,
        onPointerLeave: onRowPointerLeave,
        "data-state": open ? "open" : "closed",
        children: [
          /* @__PURE__ */ jsx("span", { className: "menu__submenu-label", children: label }),
          /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "menu__submenu-caret", children: "\u25B8" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(
      "div",
      {
        ref: setPanelRef,
        role: "menu",
        className: "menu__submenu",
        "aria-label": label,
        "data-keep-clear": "true",
        style: {
          position: "fixed",
          top: y ?? 0,
          left: x ?? 0,
          visibility: x === null || y === null ? "hidden" : void 0
        },
        onKeyDown: onSubKeyDown,
        onPointerEnter: onPanelPointerEnter,
        onPointerLeave: onPanelPointerLeave,
        children: renderItemsWithRefs(children, subItemRefs, subRefs, tabIndex)
      }
    ) })
  ] });
}
function classifyType(type) {
  if (type === MenuItem) return { kind: "focusable", tag: "MenuItem" };
  if (type === MenuSubMenu) return { kind: "focusable", tag: "MenuSubMenu" };
  if (type === MenuRadioItem) return { kind: "focusable", tag: "MenuRadioItem" };
  if (type === MenuItemCheckbox) return { kind: "focusable", tag: "MenuItemCheckbox" };
  if (type === MenuGroup) return { kind: "wrapper", tag: "MenuGroup" };
  if (type === MenuRadioGroup) return { kind: "wrapper", tag: "MenuRadioGroup" };
  return null;
}
function collectFocusable(children) {
  const out = [];
  const visit = (node) => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      const cls = classifyType(child.type);
      if (!cls) return;
      const childProps = child.props;
      if (cls.kind === "wrapper") {
        visit(childProps.children);
        return;
      }
      const rawLabel = String(childProps.label ?? "");
      const rawTextValue = String(
        childProps.textValue ?? rawLabel
      );
      if (cls.tag === "MenuItem" || cls.tag === "MenuRadioItem") {
        out.push({
          label: rawLabel,
          textValue: rawTextValue,
          onActivate: childProps.onSelect,
          disabled: Boolean(childProps.disabled)
        });
      } else if (cls.tag === "MenuItemCheckbox") {
        const checked = Boolean(childProps.checked);
        const onCheckedChange = childProps.onCheckedChange;
        out.push({
          label: rawLabel,
          textValue: rawTextValue,
          onActivate: onCheckedChange ? () => onCheckedChange(!checked) : void 0,
          disabled: Boolean(childProps.disabled)
        });
      } else {
        out.push({
          label: rawLabel,
          textValue: rawTextValue,
          disabled: Boolean(childProps.disabled)
        });
      }
    });
  };
  visit(children);
  return out;
}
function renderItemsWithRefs(children, itemRefs, focusable, tabIndex) {
  const counter = { i: 0 };
  const walk = (nodes) => Children.map(nodes, (child) => {
    if (!isValidElement(child)) return child;
    const cls = classifyType(child.type);
    if (!cls) return child;
    const childProps = child.props;
    if (cls.kind === "wrapper") {
      const newKids = walk(childProps.children);
      return cloneElement(child, {
        ...childProps,
        children: newKids
      });
    }
    const i = counter.i++;
    const target = itemRefs[i];
    const existingRef = child.ref;
    const mergedRef = (node) => {
      if (target) target.current = node;
      if (typeof existingRef === "function") existingRef(node);
      else if (existingRef && "current" in existingRef) {
        existingRef.current = node;
      }
    };
    const merged = {
      ref: mergedRef,
      tabIndex: tabIndex(i),
      ...childProps
    };
    return cloneElement(child, merged);
  });
  return walk(children);
}
var MenuBarContext = createContext(null);
function MenuBarMenu(props) {
  const validated = validateInput(
    MenuBarMenuPropsSchema,
    "MenuBarMenu",
    props
  );
  const { label, disabled = false, className, id, testId, children } = validated;
  const index = props.__menubarIndex ?? 0;
  const bar = useContext(MenuBarContext);
  if (!bar) {
    warn({
      fn: "MenuBarMenu",
      code: "WEB_MENU_TRIGGER_DETACHED",
      message: "MenuBarMenu rendered outside <MenuBar>"
    });
  }
  const isOpen = bar ? bar.openIndex === index : false;
  const setTriggerRef = useCallback(
    (node) => {
      bar?.registerTrigger(index, node);
    },
    [bar, index]
  );
  const onOpenChange = useCallback(
    (open) => {
      bar?.setOpen(open ? index : null);
    },
    [bar, index]
  );
  const cls = [
    "menu__bar-item",
    disabled && "menu__bar-item--disabled",
    isOpen && "menu__bar-item--open",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(
    Menu,
    {
      open: isOpen,
      onOpenChange,
      placement: bar?.placement ?? "bottom-start",
      id,
      children: [
        /* @__PURE__ */ jsx(
          MenuTrigger,
          {
            asChild: true,
            ref: setTriggerRef,
            ariaLabel: label,
            children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                role: "menuitem",
                className: cls,
                "data-menubar-index": index,
                "data-testid": testId,
                disabled: disabled || void 0,
                tabIndex: bar ? bar.tabIndexFor(index) : 0,
                onPointerEnter: () => bar?.onTriggerHover(index),
                onFocus: () => bar?.setFocused(index),
                children: /* @__PURE__ */ jsx("span", { className: "menu__bar-item-label", children: label })
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(MenuContent, { ariaLabel: label, children })
      ]
    }
  );
}
function MenuBar(props) {
  const validated = validateInput(
    MenuBarPropsSchema,
    "MenuBar",
    props
  );
  const {
    children,
    ariaLabel,
    id,
    className,
    loop = true,
    placement = "bottom-start",
    testId
  } = validated;
  const autoId = useId();
  const menubarId = id ?? autoId;
  const [openIndex, setOpenIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const count = useMemo(() => {
    let n = 0;
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.type === MenuBarMenu) n += 1;
    });
    return n;
  }, [children]);
  const triggerRefs = useRef([]);
  if (triggerRefs.current.length !== count) {
    triggerRefs.current = Array.from(
      { length: count },
      (_, i) => triggerRefs.current[i] ?? { current: null }
    );
  }
  const { tabIndex: rovingTabIndex, onKeyDown: rovingKeyDown } = useRovingTabIndex(triggerRefs.current, {
    orientation: "horizontal",
    loop,
    activeIndex: focusedIndex,
    manualActivation: true,
    onMove: (i) => setFocusedIndex(i)
  });
  const registerTrigger = useCallback(
    (index, node) => {
      const slot = triggerRefs.current[index];
      if (slot) slot.current = node;
    },
    []
  );
  const setOpen = useCallback((index) => setOpenIndex(index), []);
  const setFocused = useCallback((index) => setFocusedIndex(index), []);
  const onTriggerHover = useCallback((index) => {
    setOpenIndex((prev) => prev !== null && prev !== index ? index : prev);
    setFocusedIndex(index);
  }, []);
  const tabIndexFor = useCallback(
    (index) => rovingTabIndex(index),
    [rovingTabIndex]
  );
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Home" || e.key === "End") {
        rovingKeyDown(e);
      }
    },
    [rovingKeyDown]
  );
  const ctx = useMemo(
    () => ({
      openIndex,
      focusedIndex,
      setOpen,
      setFocused,
      registerTrigger,
      onTriggerHover,
      tabIndexFor,
      placement
    }),
    [
      openIndex,
      focusedIndex,
      setOpen,
      setFocused,
      registerTrigger,
      onTriggerHover,
      tabIndexFor,
      placement
    ]
  );
  let ordinal = -1;
  const rendered = Children.map(children, (child) => {
    if (isValidElement(child) && child.type === MenuBarMenu) {
      ordinal += 1;
      return cloneElement(child, {
        __menubarIndex: ordinal
      });
    }
    return child;
  });
  const cls = ["menu__bar", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(MenuBarContext.Provider, { value: ctx, children: /* @__PURE__ */ jsx(
    "div",
    {
      role: "menubar",
      "aria-orientation": "horizontal",
      "aria-label": ariaLabel,
      id: menubarId,
      className: cls,
      "data-testid": testId,
      onKeyDown,
      ...withConformance(WEB_MENU_LIB, LIB_VERSION),
      children: rendered
    }
  ) });
}
var selfCheckInputSchema = z.object({
  opts: SelfCheckOptionsSchema.optional()
}).strict();
var ConformanceSeveritySchema = z.enum(["critical", "major", "minor", "info"]);
var ConformanceCategorySchema = z.enum([
  "theme",
  "stylesheet",
  "a11y",
  "layout",
  "i18n",
  "cardinality",
  "identity",
  "schema",
  "flow",
  "deprecation",
  "perf"
]);
var SelfCheckFindingSchema = z.object({
  libId: z.string().min(1),
  code: z.string().min(1),
  category: ConformanceCategorySchema,
  severity: ConformanceSeveritySchema,
  message: z.string().min(1),
  selector: z.string().optional(),
  suggestedFix: z.string().optional(),
  extras: z.record(z.unknown()).optional()
}).strict();
var LIB_VERSIONSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/, "LIB_VERSION must be semver");
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-menu",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in menu.css:36-39 — JS-positioned fixed surface, fixed
    // min/max-width clamp, NO @media. Honest baseline: 'full' every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

export { ConformanceCategorySchema, ConformanceSeveritySchema, LIB_VERSION, LIB_VERSIONSchema, Menu, MenuBar, MenuBarMenu, MenuBarMenuPropsSchema, MenuBarPropsSchema, MenuContent, MenuContentPropsSchema, MenuGroup, MenuItem, MenuItemCheckbox, MenuItemIndicator, MenuItemPropsSchema, MenuLabel, MenuPlacementSchema, MenuPropsSchema, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuSeparatorPropsSchema, MenuSubMenu, MenuSubMenuPropsSchema, MenuTrigger, MenuTriggerPropsSchema, RESPONSIVE_CONTRACT, SelfCheckFindingSchema, SelfCheckOptionsSchema, UseMenuOptionsSchema, selfCheck, selfCheckInputSchema, useMenu };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map